<template>
  <a class="floating-container" @click.prevent="scrollToTop" v-show="showButton" role="button" tabindex="0" aria-label="Back to top" @keydown.space.prevent="scrollToTop" @keydown.enter.prevent="scrollToTop">
    <div class="floating-button">
      <font-awesome-icon icon="fa-solid fa-angles-up"/>
    </div>
  </a>
</template>

<script>
export default {
  name: 'FloatingScrollTopButton',
  data() {
    return {
      showButton: false
    }
  },
  mounted() {
    // Initialize containers array
    this.scrollContainers = [];

    // Set up container detection
    this.setupScrollContainers();

    // Set up a MutationObserver to detect when new containers are added to the DOM
    this.setupMutationObserver();

    // Initial check
    this.handleScroll();
  },

  created() {
    // Create methods that will be used in mounted
    this.setupScrollContainers = () => {
      // Clear existing event listeners
      if (this.scrollContainers && this.scrollContainers.length > 0) {
        this.scrollContainers.forEach(container => {
          container.removeEventListener('scroll', this.handleScroll);
        });
      }

      // Reset containers array
      this.scrollContainers = [];

      // Helper to ensure we only track elements that actually scroll
      const isScrollable = (el) => {
        if (!el || el === window) return false;
        const style = getComputedStyle(el);
        const overflowY = style.overflowY;
        const canScroll = (overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight;
        return canScroll;
      };

      // Check for navigation-route-view
      const navigationRouteView = document.querySelector('.navigation-route-view');
      if (navigationRouteView && isScrollable(navigationRouteView)) {
        this.scrollContainers.push(navigationRouteView);
        navigationRouteView.addEventListener('scroll', this.handleScroll, { passive: true });
      }

      // Check for FullPage containers (there might be multiple)
      const fullPageContainers = document.querySelectorAll('.page');
      if (fullPageContainers.length > 0) {
        fullPageContainers.forEach(container => {
          if (isScrollable(container)) {
            this.scrollContainers.push(container);
            container.addEventListener('scroll', this.handleScroll, { passive: true });
          }
        });
      }

      // Check for PageWithBackBtn containers (there might be multiple)
      const pageWithBackBtnContainers = document.querySelectorAll('.page1');
      if (pageWithBackBtnContainers.length > 0) {
        pageWithBackBtnContainers.forEach(container => {
          if (isScrollable(container)) {
            this.scrollContainers.push(container);
            container.addEventListener('scroll', this.handleScroll, { passive: true });
          }
        });
      }

      // Check for Finance page containers (app-scroll-wrapper)
      const financeContainers = document.querySelectorAll('.app-scroll-wrapper');
      if (financeContainers.length > 0) {
        financeContainers.forEach(container => {
          if (isScrollable(container)) {
            this.scrollContainers.push(container);
            container.addEventListener('scroll', this.handleScroll, { passive: true });
          }
        });
      }

      // Check for Finance dashboard containers (fin-dash)
      const financeDashboardContainers = document.querySelectorAll('.fin-dash');
      if (financeDashboardContainers.length > 0) {
        financeDashboardContainers.forEach(container => {
          if (isScrollable(container)) {
            this.scrollContainers.push(container);
            container.addEventListener('scroll', this.handleScroll, { passive: true });
          }
        });
      }

      // Always listen to window scroll as a fallback and for pages using normal window scrolling
      window.removeEventListener('scroll', this.handleScroll);
      window.addEventListener('scroll', this.handleScroll, { passive: true });
      this.usingWindowScroll = true;
    };

    this.setupMutationObserver = () => {
      // Create a MutationObserver to watch for DOM changes
      this.observer = new MutationObserver((mutations) => {
        // Check if any mutations added nodes that might be our containers
        const shouldRefresh = mutations.some(mutation => 
          mutation.addedNodes.length > 0 && 
          Array.from(mutation.addedNodes).some(node => 
            node.nodeType === 1 && // Element node
            (node.classList?.contains('navigation-route-view') ||
             node.classList?.contains('page') || 
             node.classList?.contains('page1') ||
             node.classList?.contains('app-scroll-wrapper') ||
             node.classList?.contains('fin-dash') ||
             node.querySelector?.('.navigation-route-view, .page, .page1, .app-scroll-wrapper, .fin-dash'))
          )
        );

        if (shouldRefresh) {
          // Re-setup scroll containers if relevant nodes were added
          this.setupScrollContainers();
          this.handleScroll();
        }
      });

      // Start observing the document with the configured parameters
      this.observer.observe(document.body, { 
        childList: true, 
        subtree: true 
      });
    };
  },
  beforeDestroy() {
    // Remove event listeners from all containers
    if (this.scrollContainers && this.scrollContainers.length > 0) {
      this.scrollContainers.forEach(container => {
        container.removeEventListener('scroll', this.handleScroll);
      });
    } 

    // Remove window event listener if it was being used
    if (this.usingWindowScroll) {
      window.removeEventListener('scroll', this.handleScroll);
    }

    // Disconnect the MutationObserver
    if (this.observer) {
      this.observer.disconnect();
    }
  },
  methods: {
    handleScroll() {
      // Default to not showing the button
      this.showButton = false;

      let visible = false;
      let active = null;

      // Consider all registered containers
      if (this.scrollContainers && this.scrollContainers.length > 0) {
        for (const container of this.scrollContainers) {
          if (container.scrollTop > 300) {
            visible = true;
            active = container;
            break;
          }
        }
      }

      // Also consider window scrolling in parallel
      if (!visible) {
        const winTop = window.pageYOffset || document.documentElement.scrollTop || 0;
        if (winTop > 300) {
          visible = true;
          active = null; // window is the scroller
        }
      }

      this.showButton = visible;
      this.activeScrollContainer = active;
    },
    scrollToTop() {
      // If we have an active scrolled container, scroll it to top
      if (this.activeScrollContainer) {
        this.activeScrollContainer.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
        return;
      }

      // If no active container, detect if window is scrolled and scroll it
      const winTop = window.pageYOffset || document.documentElement.scrollTop || 0;
      if (winTop > 0) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      // As a last resort, try any container that is scrolled
      if (this.scrollContainers && this.scrollContainers.length > 0) {
        for (const container of this.scrollContainers) {
          if (container.scrollTop > 0) {
            container.scrollTo({ top: 0, behavior: 'smooth' });
            break;
          }
        }
      }
    }
  }
}
</script>


<style scoped>
@import url("https://fonts.googleapis.com/css?family=Roboto");

@-webkit-keyframes come-in {
  0% {
    -webkit-transform: translatey(100px);
    transform: translatey(100px);
    opacity: 0;
  }
  30% {
    -webkit-transform: translateX(-50px) scale(0.4);
    transform: translateX(-50px) scale(0.4);
  }
  70% {
    -webkit-transform: translateX(0px) scale(1.2);
    transform: translateX(0px) scale(1.2);
  }
  100% {
    -webkit-transform: translatey(0px) scale(1);
    transform: translatey(0px) scale(1);
    opacity: 1;
  }
}

@keyframes come-in {
  0% {
    -webkit-transform: translatey(100px);
    transform: translatey(100px);
    opacity: 0;
  }
  30% {
    -webkit-transform: translateX(-50px) scale(0.4);
    transform: translateX(-50px) scale(0.4);
  }
  70% {
    -webkit-transform: translateX(0px) scale(1.2);
    transform: translateX(0px) scale(1.2);
  }
  100% {
    -webkit-transform: translatey(0px) scale(1);
    transform: translatey(0px) scale(1);
    opacity: 1;
  }
}

@-webkit-keyframes pulse {
  0% {
    -webkit-transform: scale(1);
    transform: scale(1);
    box-shadow: 0 10px 25px -5px rgba(0, 64, 128, 0.6);
  }
  50% {
    -webkit-transform: scale(1.1);
    transform: scale(1.1);
    box-shadow: 0 10px 25px -2px rgba(0, 64, 128, 0.8);
  }
  100% {
    -webkit-transform: scale(1);
    transform: scale(1);
    box-shadow: 0 10px 25px -5px rgba(0, 64, 128, 0.6);
  }
}

@keyframes pulse {
  0% {
    transform: scale(1);
    box-shadow: 0 10px 25px -5px rgba(0, 64, 128, 0.6);
  }
  50% {
    transform: scale(1.1);
    box-shadow: 0 10px 25px -2px rgba(0, 64, 128, 0.8);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 10px 25px -5px rgba(0, 64, 128, 0.6);
  }
}

.floating-container {
  position: fixed;
  width: 36px;
  height: 36px;
  bottom: 0;
  right: 0;
  margin: 16px;
  z-index: 1060; /* Above toasts */
}

/* Remove hover grow; keep subtle */
.floating-container:hover {
  height: 36px;
}

.floating-container .floating-button {
  position: absolute;
  width: 36px;
  height: 36px;
  background: var(--card-bg, #111827);
  bottom: 0;
  border-radius: var(--radius-button, 5px);
  left: 0;
  right: 0;
  margin: auto;
  color: var(--text-primary, #e5e7eb);
  line-height: 36px;
  text-align: center;
  font-size: 16px;
  z-index: 100;
  border: 1px solid var(--grid-lines, rgba(255,255,255,0.12));
  box-shadow: 0 4px 10px rgba(0,0,0,0.15);
  cursor: pointer;
  transition: 0.3s ease all;
}

.floating-container .floating-button:hover,
.floating-container .floating-button:focus {
  outline: 2px solid var(--accent-secondary, #A529FF);
  color: var(--accent-primary, #339CFF);
  transform: translateY(-1px);
}
</style>
